import type { StoryData, StoryblokComponent } from 'storyblok-js-client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { addBridge, initBridgeEventListeners } from '../storybridge.utils';
import type { StoryContent } from '../types/global.types';
import useUnmount from '../../../hooks/useUnmount';
import { useStoryblokEditorContext } from '../../../components/unlisted/StoryblokEditor';

export type Story = StoryData<
  StoryContent &
    (StoryblokComponent<string> & {
      modules?: Array<StoryblokComponent<string>>;
    })
>;

export function useStoryBlokBridge(
  originalStory: Story,
  preview?: boolean,
  locale?: string,
): Story {
  const [story, setStory] = useState<Story>(originalStory);
  const isUnMounted = useRef(false);
  const { setIsInEditorMode } = useStoryblokEditorContext();

  const updateStory = useCallback((story: Story) => {
    if (!isUnMounted.current) {
      setStory(story);
    }
  }, []);

  useUnmount(() => {
    isUnMounted.current = true;
  });

  useEffect(() => {
    // only load inside preview mode
    if (preview) {
      addBridge(() => {
        if (!isUnMounted.current) {
          initBridgeEventListeners(story, updateStory, setIsInEditorMode, locale);
        }
      });
    }
  }, [preview, locale, story, updateStory, setIsInEditorMode]);

  useEffect(() => {
    setStory(originalStory);
  }, [originalStory]);

  return story;
}
